import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ThemeEngine } from "@/components/ThemeEngine";
import { AccessGate } from "@/components/AccessGate";
import ScrollToTop from "./components/ScrollToTop";
import Layout from "./components/Layout";
import PageGuard from "./components/PageGuard";
import OrganizationHome from "./pages/OrganizationHome";
import About from "./pages/About";
import Attendees from "./pages/Attendees";
import Sponsors from "./pages/Sponsors";
import Nonprofits from "./pages/Nonprofits";
import Sandbox from "./pages/Sandbox";
import Library from "./pages/Library";
import LibraryItemDetail from "./pages/LibraryItemDetail";
import Community from "./pages/Community";
import HowItWorks from "./pages/HowItWorks";
import Events from "./pages/Events";
import EventDetailPublic from "./pages/EventDetailPublic";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import LibraryTabs from "./pages/admin/LibraryTabs";
import EventsHub from "./pages/admin/EventsHub";
import AudienceHub from "./pages/admin/AudienceHub";
import AnalyticsHub from "./pages/admin/AnalyticsHub";
import EventDetail from "./pages/admin/EventDetail";
import Users from "./pages/admin/Users";
import Tags from "./pages/admin/Tags";
import TagAnalytics from "./pages/admin/TagAnalytics";
import EmailTemplates from "./pages/admin/EmailTemplates";
import Integrations from "./pages/admin/Integrations";
import Settings from "./pages/admin/Settings";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import FeatureRequests from "./pages/FeatureRequests";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
// Super Admin pages
import SuperAdminLayout from "./pages/super-admin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/super-admin/Dashboard";
import Organizations from "./pages/super-admin/Organizations";
import OrganizationDetail from "./pages/super-admin/OrganizationDetail";
import Domains from "./pages/super-admin/Domains";
import AllUsers from "./pages/super-admin/AllUsers";
import Analytics from "./pages/super-admin/Analytics";
import SuperAdminIntegrations from "./pages/super-admin/Integrations";
// User Dashboard pages
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import DashboardChat from "./pages/dashboard/DashboardChat";
import SavedResources from "./pages/dashboard/SavedResources";
import MyCollection from "./pages/dashboard/MyCollection";
import SavedPrompts from "./pages/dashboard/SavedPrompts";
import UserSubmissions from "./pages/dashboard/UserSubmissions";
import UserInvites from "./pages/dashboard/UserInvites";
import Challenges from "./pages/Challenges";
import UserChallenges from "./pages/dashboard/UserChallenges";
import UserProfile from "./pages/dashboard/UserProfile";
import UserEvents from "./pages/dashboard/UserEvents";
import UserFollowing from "./pages/dashboard/UserFollowing";
import NotificationSettings from "./pages/dashboard/NotificationSettings";
import AcceptTeamInvite from "./pages/AcceptTeamInvite";
import RegisterSuccess from "./pages/RegisterSuccess";
import SuperAdminContacts from "./pages/super-admin/Contacts";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds - data stays fresh, no skeleton on tab switch
      refetchOnWindowFocus: false, // Prevent refetch when switching browser tabs
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <OrganizationProvider>
        <ThemeEngine />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AccessGate>
            <Routes>
              <Route path="/" element={<PageGuard pageId="home"><Layout><OrganizationHome /></Layout></PageGuard>} />
              <Route path="/about" element={<PageGuard pageId="about"><Layout><About /></Layout></PageGuard>} />
              <Route path="/attendees" element={<PageGuard pageId="attendees"><Layout><Attendees /></Layout></PageGuard>} />
              <Route path="/sponsors" element={<PageGuard pageId="sponsors"><Layout><Sponsors /></Layout></PageGuard>} />
              <Route path="/nonprofits" element={<PageGuard pageId="nonprofits"><Layout><Nonprofits /></Layout></PageGuard>} />
              <Route path="/how-it-works" element={<PageGuard pageId="how-it-works"><Layout><HowItWorks /></Layout></PageGuard>} />
              <Route path="/events" element={<PageGuard pageId="events"><Layout><Events /></Layout></PageGuard>} />
              <Route path="/events/:slug" element={<Layout><EventDetailPublic /></Layout>} />
              <Route path="/sandbox" element={<PageGuard pageId="sandbox"><Layout><Sandbox /></Layout></PageGuard>} />
              <Route path="/challenges" element={<PageGuard pageId="challenges"><Layout><Challenges /></Layout></PageGuard>} />
              <Route path="/library" element={<PageGuard pageId="library"><Layout><Library /></Layout></PageGuard>} />
              <Route path="/library/:slug" element={<Layout><LibraryItemDetail /></Layout>} />
              <Route path="/community" element={<PageGuard pageId="community"><Layout><Community /></Layout></PageGuard>} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/payment-success" element={<Layout><PaymentSuccess /></Layout>} />
              <Route path="/payment-cancel" element={<Layout><PaymentCancel /></Layout>} />
              <Route path="/feature-requests" element={<PageGuard pageId="feature-requests"><Layout><FeatureRequests /></Layout></PageGuard>} />
              <Route path="/invite/accept/:token" element={<AcceptTeamInvite />} />
              <Route path="/register/success" element={<Layout><RegisterSuccess /></Layout>} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                {/* Content Hub (Library + Pages) */}
                <Route path="content" element={<LibraryTabs />} />
                <Route path="content/tags" element={<Tags />} />
                <Route path="content/tags/analytics" element={<TagAnalytics />} />
                {/* Events Hub */}
                <Route path="events" element={<EventsHub />} />
                <Route path="events/:eventId" element={<EventDetail />} />
                {/* Audience Hub */}
                <Route path="audience" element={<AudienceHub />} />
                {/* Analytics Hub */}
                <Route path="analytics" element={<AnalyticsHub />} />
                {/* Settings */}
                <Route path="settings/users" element={<Users />} />
                <Route path="settings/email-templates" element={<EmailTemplates />} />
                <Route path="settings/integrations" element={<Integrations />} />
                <Route path="settings" element={<Settings />} />
                {/* LEGACY REDIRECTS - Preserve old bookmarks */}
                <Route path="library" element={<Navigate to="/admin/content" replace />} />
                <Route path="library/*" element={<Navigate to="/admin/content" replace />} />
                <Route path="library/tags" element={<Navigate to="/admin/content/tags" replace />} />
                <Route path="library/tags/analytics" element={<Navigate to="/admin/content/tags/analytics" replace />} />
                <Route path="pages" element={<Navigate to="/admin/content?tab=pages" replace />} />
                <Route path="contacts" element={<Navigate to="/admin/audience" replace />} />
                <Route path="contact-submissions" element={<Navigate to="/admin/audience?tab=inbox" replace />} />
                <Route path="sandbox-usage" element={<Navigate to="/admin/analytics?tab=planner" replace />} />
                <Route path="skill-analytics" element={<Navigate to="/admin/analytics?tab=skills" replace />} />
                <Route path="ai-usage" element={<Navigate to="/admin/analytics?tab=ai" replace />} />
                <Route path="knowledge-base" element={<Navigate to="/admin/analytics?tab=knowledge" replace />} />
                <Route path="users" element={<Navigate to="/admin/settings/users" replace />} />
                <Route path="email-templates" element={<Navigate to="/admin/settings/email-templates" replace />} />
                <Route path="integrations" element={<Navigate to="/admin/settings/integrations" replace />} />
                <Route path="tags" element={<Navigate to="/admin/content/tags" replace />} />
                <Route path="tags/analytics" element={<Navigate to="/admin/content/tags/analytics" replace />} />
                <Route path="settings/event" element={<Navigate to="/admin/events" replace />} />
                <Route path="registrations" element={<Navigate to="/admin/events" replace />} />
                <Route path="settings/integrations" element={<Navigate to="/admin/settings/integrations" replace />} />
                <Route path="settings/seo" element={<Navigate to="/admin/content?tab=pages" replace />} />
              </Route>
              {/* Super Admin Routes */}
              <Route path="/super-admin" element={<ProtectedRoute><SuperAdminLayout /></ProtectedRoute>}>
                <Route index element={<SuperAdminDashboard />} />
                <Route path="organizations" element={<Organizations />} />
                <Route path="organizations/:id" element={<OrganizationDetail />} />
                <Route path="domains" element={<Domains />} />
                <Route path="users" element={<AllUsers />} />
                <Route path="integrations" element={<SuperAdminIntegrations />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="contacts" element={<SuperAdminContacts />} />
              </Route>
              {/* User Dashboard Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<DashboardHome />} />
                <Route path="chat" element={<DashboardChat />} />
                <Route path="collection" element={<MyCollection />} />
                <Route path="saved" element={<SavedResources />} />
                <Route path="chat-history" element={<SavedPrompts />} />
                <Route path="prompts" element={<SavedPrompts />} />
                <Route path="submissions" element={<UserSubmissions />} />
                <Route path="events" element={<UserEvents />} />
                <Route path="challenges" element={<UserChallenges />} />
                <Route path="following" element={<UserFollowing />} />
                <Route path="invites" element={<UserInvites />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="notifications" element={<NotificationSettings />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AccessGate>
        </BrowserRouter>
      </OrganizationProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
